import { useAuth } from "./useAuth";
import {
  DEVICE_ACCESS_PERMISSIONS,
  DEVICE_DISTRIBUTION_ACCESS_PERMISSIONS,
  DEVICE_DELETE_ACCESS_PERMISSIONS,
  DEVICE_DETAIL_ACCESS_PERMISSIONS,
  DEVICE_EXPORT_ACCESS_PERMISSIONS,
  DEVICE_LOCATION_UPDATE_ACCESS_PERMISSIONS,
  DEVICE_UPDATE_ACCESS_PERMISSIONS,
  HISTORY_ACCESS_PERMISSIONS,
  HISTORY_DETAIL_ACCESS_PERMISSIONS,
  HOME_RECOGNITION_DASHBOARD_ACCESS_PERMISSIONS,
  HOME_VIEW_ACCESS_PERMISSIONS,
  IMAGE_DETECTION_ACCESS_PERMISSIONS,
  hasAnyPermissions,
  hasPermission,
  MANUAL_RECOGNITION_ACCESS_PERMISSIONS,
  MANUAL_RECOGNITION_SUBMIT_PERMISSIONS,
  MENDER_OTA_CONFIG_READ_ACCESS_PERMISSIONS,
  MENDER_OTA_CONFIG_UPDATE_ACCESS_PERMISSIONS,
  MODEL_ACTIVATE_ACCESS_PERMISSIONS,
  MODEL_EVALUATE_ACCESS_PERMISSIONS,
  MODEL_VALIDATION_READ_ACCESS_PERMISSIONS,
  PACKAGE_DEVICE_ACCESS_PERMISSIONS,
  PACKAGE_MANAGEMENT_ACCESS_PERMISSIONS,
  PAYMENT_ACCESS_PERMISSIONS,
  PAYMENT_DETAIL_ACCESS_PERMISSIONS,
  PAYMENT_MERCHANT_MANAGEMENT_ACCESS_PERMISSIONS,
  OPERATION_LOG_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_ARTIFACT_CREATE_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_ARTIFACT_DELETE_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_ARTIFACT_DISABLE_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_ARTIFACT_ENABLE_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_ARTIFACT_READ_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_ARTIFACT_UPDATE_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_BATCH_CANCEL_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_BATCH_CREATE_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_BATCH_DETAIL_READ_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_BATCH_READ_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_BATCH_SYNC_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_BINDING_DETAIL_READ_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_BINDING_READ_ACCESS_PERMISSIONS,
  OPERATIONS_DEVICE_UPGRADE_DEVICE_SYNC_ACCESS_PERMISSIONS,
  PROJECT_CREATE_ACCESS_PERMISSIONS,
  PROJECT_DELETE_ACCESS_PERMISSIONS,
  PROJECT_VIEWER_BOARD_ACCESS_PERMISSIONS,
  PROJECT_MANAGEMENT_ACCESS_PERMISSIONS,
  PROJECT_READ_ACCESS_PERMISSIONS,
  PROJECT_STATUS_UPDATE_ACCESS_PERMISSIONS,
  PROJECT_UPDATE_ACCESS_PERMISSIONS,
  PROJECT_VIEWER_ACCOUNT_STATUS_UPDATE_ACCESS_PERMISSIONS,
  PROJECT_VIEWER_ACCOUNT_UPDATE_ACCESS_PERMISSIONS,
  PROJECT_VIEWER_SCOPE_GRANT_ACCESS_PERMISSIONS,
  PROJECT_VIEWER_SCOPE_READ_ACCESS_PERMISSIONS,
  REGION_MANAGEMENT_ACCESS_PERMISSIONS,
  ROLE_MANAGEMENT_ACCESS_PERMISSIONS,
  ROLE_PERMISSION_READ_ACCESS_PERMISSIONS,
  STATISTICS_EXPORT_ACCESS_PERMISSIONS,
  STATISTICS_SPECIES_ANALYSIS_ACCESS_PERMISSIONS,
  STATISTICS_TREND_ANALYSIS_ACCESS_PERMISSIONS,
  STATISTICS_ACCESS_PERMISSIONS,
  SYSTEM_SETTINGS_ACCESS_PERMISSIONS,
  USER_API_CREDENTIALS_MANAGE_ACCESS_PERMISSIONS,
  USER_MANAGEMENT_ACCESS_PERMISSIONS,
  USER_PASSWORD_UPDATE_ACCESS_PERMISSIONS,
} from "../utils/permissions";
import type { UserResponse } from "../types/user";

const PROJECT_SCOPED_ROLE_NAMES = new Set(["project_manager", "project_viewer"]);

const hasScopedProjectIds = (values: readonly number[] | undefined): boolean =>
  Array.isArray(values) && values.length > 0;

export const isProjectScopedAccountForUser = (
  user: UserResponse | null | undefined,
): boolean => {
  if (!user) {
    return false;
  }

  if (user.account_type === "project" || user.home_project_id != null) {
    return true;
  }

  if (user.has_global_project_visibility === true) {
    return false;
  }

  if (
    hasScopedProjectIds(user.managed_project_ids) ||
    hasScopedProjectIds(user.visible_project_ids)
  ) {
    return true;
  }

  const roleName = (user.role || "").trim().toLowerCase();
  return PROJECT_SCOPED_ROLE_NAMES.has(roleName);
};

export const canManageProjectsForUser = (
  user: UserResponse | null | undefined,
): boolean => Boolean(user?.can_manage_projects);

export const canGrantProjectViewersForUser = (
  user: UserResponse | null | undefined,
): boolean =>
  Boolean(user?.can_grant_project_viewers) ||
  hasAnyPermissions(user, PROJECT_VIEWER_SCOPE_GRANT_ACCESS_PERMISSIONS);

export const canAccessProjectViewerBoardForUser = (
  user: UserResponse | null | undefined,
): boolean =>
  Boolean(user?.can_access_project_viewer_board) ||
  hasAnyPermissions(user, PROJECT_VIEWER_BOARD_ACCESS_PERMISSIONS);

export const canAccessProjectViewerManagementForUser = (
  user: UserResponse | null | undefined,
): boolean =>
  canAccessProjectViewerBoardForUser(user);

export const canAccessModelManagementForUser = (
  user: UserResponse | null | undefined,
): boolean => user?.role === "admin";

export const canAccessModelValidationForUser = (
  user: UserResponse | null | undefined,
): boolean => hasAnyPermissions(user, MODEL_VALIDATION_READ_ACCESS_PERMISSIONS);

export const usePermissions = () => {
  const { user, authStatus } = useAuth();
  const permissionUser = authStatus === "authenticated" ? user : null;
  const canReviewHistory = Boolean(permissionUser?.can_review_history);
  const canExportHistoryDataset = Boolean(
    permissionUser?.can_export_history_dataset,
  );
  const canAccessManualRecognition = Boolean(
    permissionUser?.can_access_manual_recognition,
  ) || hasAnyPermissions(permissionUser, MANUAL_RECOGNITION_ACCESS_PERMISSIONS);
  const canSubmitManualRecognition = hasAnyPermissions(
    permissionUser,
    MANUAL_RECOGNITION_SUBMIT_PERMISSIONS,
  );
  const canDeleteHistory = hasPermission(permissionUser, "history:delete");
  const canReadHistoryDetails = hasAnyPermissions(
    permissionUser,
    HISTORY_DETAIL_ACCESS_PERMISSIONS,
  );

  const canReadUsers = hasPermission(permissionUser, "user:read");
  const canCreateUsers = hasPermission(permissionUser, "user:create");
  const canUpdateUsers = hasPermission(permissionUser, "user:update");
  const canUpdateUserPasswords = hasAnyPermissions(
    permissionUser,
    USER_PASSWORD_UPDATE_ACCESS_PERMISSIONS,
  );
  const canManageUserApiCredentials = hasAnyPermissions(
    permissionUser,
    USER_API_CREDENTIALS_MANAGE_ACCESS_PERMISSIONS,
  );
  const canDeleteUsers = hasPermission(permissionUser, "user:delete");
  const canReadRoles = hasAnyPermissions(
    permissionUser,
    ROLE_MANAGEMENT_ACCESS_PERMISSIONS,
  );
  const canViewRolePermissions = hasAnyPermissions(
    permissionUser,
    ROLE_PERMISSION_READ_ACCESS_PERMISSIONS,
  );
  const canCreateRoles = hasPermission(permissionUser, "rbac:role:create");
  const canUpdateRoles = hasPermission(permissionUser, "rbac:role:update");
  const canDeleteRoles = hasPermission(permissionUser, "rbac:role:delete");
  const canAssignRoles = hasPermission(permissionUser, "rbac:role:assign");
  const canReadRegions = hasPermission(permissionUser, "region:read");
  const canCreateRegions = hasPermission(permissionUser, "region:create");
  const canUpdateRegions = hasPermission(permissionUser, "region:update");
  const canDeleteRegions = hasPermission(permissionUser, "region:delete");
  const canReadDevices = hasPermission(permissionUser, "device:read");
  const canReadDeviceDetails = hasAnyPermissions(
    permissionUser,
    DEVICE_DETAIL_ACCESS_PERMISSIONS,
  );
  const canUpdateDevices = hasAnyPermissions(
    permissionUser,
    DEVICE_UPDATE_ACCESS_PERMISSIONS,
  );
  const canUpdateDeviceLocation = hasAnyPermissions(
    permissionUser,
    DEVICE_LOCATION_UPDATE_ACCESS_PERMISSIONS,
  );
  const canDeleteDevices = hasAnyPermissions(
    permissionUser,
    DEVICE_DELETE_ACCESS_PERMISSIONS,
  );
  const canExportDevices = hasAnyPermissions(
    permissionUser,
    DEVICE_EXPORT_ACCESS_PERMISSIONS,
  );
  const isProjectAccount = permissionUser?.account_type === "project";
  const isProjectScopedAccount = isProjectScopedAccountForUser(permissionUser);
  const canManageProjects = canManageProjectsForUser(permissionUser);
  const canReadProjects = hasAnyPermissions(
    permissionUser,
    PROJECT_READ_ACCESS_PERMISSIONS,
  );
  const canCreateProjects = hasAnyPermissions(
    permissionUser,
    PROJECT_CREATE_ACCESS_PERMISSIONS,
  );
  const canUpdateProjects = hasAnyPermissions(
    permissionUser,
    PROJECT_UPDATE_ACCESS_PERMISSIONS,
  );
  const canUpdateProjectStatus = hasAnyPermissions(
    permissionUser,
    PROJECT_STATUS_UPDATE_ACCESS_PERMISSIONS,
  );
  const canDeleteProjects = hasAnyPermissions(
    permissionUser,
    PROJECT_DELETE_ACCESS_PERMISSIONS,
  );
  const canReadProjectViewerScopes = hasAnyPermissions(
    permissionUser,
    PROJECT_VIEWER_SCOPE_READ_ACCESS_PERMISSIONS,
  );
  const canGrantProjectViewers =
    canGrantProjectViewersForUser(permissionUser);
  const canUpdateProjectViewerAccounts = hasAnyPermissions(
    permissionUser,
    PROJECT_VIEWER_ACCOUNT_UPDATE_ACCESS_PERMISSIONS,
  );
  const canUpdateProjectViewerAccountStatus = hasAnyPermissions(
    permissionUser,
    PROJECT_VIEWER_ACCOUNT_STATUS_UPDATE_ACCESS_PERMISSIONS,
  );
  const canAccessProjectViewerBoard =
    canAccessProjectViewerBoardForUser(permissionUser);
  const canAccessProjectManagement = hasAnyPermissions(
    permissionUser,
    PROJECT_MANAGEMENT_ACCESS_PERMISSIONS,
  );
  const canAccessProjectViewerManagement =
    canAccessProjectViewerManagementForUser(permissionUser);
  const canAccessPackageManagement = hasAnyPermissions(
    permissionUser,
    PACKAGE_MANAGEMENT_ACCESS_PERMISSIONS,
  );
  const canAccessModelManagement =
    canAccessModelManagementForUser(permissionUser);
  const canAccessModelValidation =
    canAccessModelValidationForUser(permissionUser);
  const canEvaluateModels = hasAnyPermissions(
    permissionUser,
    MODEL_EVALUATE_ACCESS_PERMISSIONS,
  );
  const canActivateModels = hasAnyPermissions(
    permissionUser,
    MODEL_ACTIVATE_ACCESS_PERMISSIONS,
  );
  const canReadPackageDevices = hasAnyPermissions(
    permissionUser,
    PACKAGE_DEVICE_ACCESS_PERMISSIONS,
  );
  const canReadPaymentDetails = hasAnyPermissions(
    permissionUser,
    PAYMENT_DETAIL_ACCESS_PERMISSIONS,
  );
  const canReadMenderOtaConfig = hasAnyPermissions(
    permissionUser,
    MENDER_OTA_CONFIG_READ_ACCESS_PERMISSIONS,
  );
  const canUpdateMenderOtaConfig = hasAnyPermissions(
    permissionUser,
    MENDER_OTA_CONFIG_UPDATE_ACCESS_PERMISSIONS,
  );
  const canReadDeviceUpgrades = hasAnyPermissions(
    permissionUser,
    OPERATIONS_DEVICE_UPGRADE_ACCESS_PERMISSIONS,
  );
  const canReadDeviceUpgradeBindings = hasAnyPermissions(
    permissionUser,
    OPERATIONS_DEVICE_UPGRADE_BINDING_READ_ACCESS_PERMISSIONS,
  );
  const canReadDeviceUpgradeBindingDetails = hasAnyPermissions(
    permissionUser,
    OPERATIONS_DEVICE_UPGRADE_BINDING_DETAIL_READ_ACCESS_PERMISSIONS,
  );
  const canReadDeviceUpgradeArtifacts = hasAnyPermissions(
    permissionUser,
    OPERATIONS_DEVICE_UPGRADE_ARTIFACT_READ_ACCESS_PERMISSIONS,
  );
  const canReadDeviceUpgradeBatches = hasAnyPermissions(
    permissionUser,
    OPERATIONS_DEVICE_UPGRADE_BATCH_READ_ACCESS_PERMISSIONS,
  );
  const canReadDeviceUpgradeBatchDetails = hasAnyPermissions(
    permissionUser,
    OPERATIONS_DEVICE_UPGRADE_BATCH_DETAIL_READ_ACCESS_PERMISSIONS,
  );
  const canSyncDeviceUpgradeDevices = hasAnyPermissions(
    permissionUser,
    OPERATIONS_DEVICE_UPGRADE_DEVICE_SYNC_ACCESS_PERMISSIONS,
  );
  const canCreateDeviceUpgradeArtifact = hasAnyPermissions(
    permissionUser,
    OPERATIONS_DEVICE_UPGRADE_ARTIFACT_CREATE_ACCESS_PERMISSIONS,
  );
  const canUpdateDeviceUpgradeArtifact = hasAnyPermissions(
    permissionUser,
    OPERATIONS_DEVICE_UPGRADE_ARTIFACT_UPDATE_ACCESS_PERMISSIONS,
  );
  const canEnableDeviceUpgradeArtifact = hasAnyPermissions(
    permissionUser,
    OPERATIONS_DEVICE_UPGRADE_ARTIFACT_ENABLE_ACCESS_PERMISSIONS,
  );
  const canDisableDeviceUpgradeArtifact = hasAnyPermissions(
    permissionUser,
    OPERATIONS_DEVICE_UPGRADE_ARTIFACT_DISABLE_ACCESS_PERMISSIONS,
  );
  const canDeleteDeviceUpgradeArtifact = hasAnyPermissions(
    permissionUser,
    OPERATIONS_DEVICE_UPGRADE_ARTIFACT_DELETE_ACCESS_PERMISSIONS,
  );
  const canCreateDeviceUpgradeBatch = hasAnyPermissions(
    permissionUser,
    OPERATIONS_DEVICE_UPGRADE_BATCH_CREATE_ACCESS_PERMISSIONS,
  );
  const canSyncDeviceUpgradeBatch = hasAnyPermissions(
    permissionUser,
    OPERATIONS_DEVICE_UPGRADE_BATCH_SYNC_ACCESS_PERMISSIONS,
  );
  const canCancelDeviceUpgradeBatch = hasAnyPermissions(
    permissionUser,
    OPERATIONS_DEVICE_UPGRADE_BATCH_CANCEL_ACCESS_PERMISSIONS,
  );
  const isPlatformAccount = !isProjectAccount;

  return {
    permissions: permissionUser?.permissions || [],
    hasPermission: (permission: string) => hasPermission(permissionUser, permission),
    hasAnyPermissions: (permissions: readonly string[]) =>
      hasAnyPermissions(permissionUser, permissions),
    canAccessHome: hasAnyPermissions(
      permissionUser,
      HOME_VIEW_ACCESS_PERMISSIONS,
    ),
    canAccessHomeDashboard: hasAnyPermissions(
      permissionUser,
      HOME_RECOGNITION_DASHBOARD_ACCESS_PERMISSIONS,
    ),
    canReviewHistory,
    canAccessManualRecognition,
    canSubmitManualRecognition,
    canReadHistoryDetails,
    canExportHistoryDataset,
    canDeleteHistory,
    canReadUsers,
    canCreateUsers,
    canUpdateUsers,
    canUpdateUserPasswords,
    canManageUserApiCredentials,
    canDeleteUsers,
    canReadRoles,
    canViewRolePermissions,
    canCreateRoles,
    canUpdateRoles,
    canDeleteRoles,
    canAssignRoles,
    canReadRegions,
    canCreateRegions,
    canUpdateRegions,
    canDeleteRegions,
    canReadDevices,
    canReadDeviceDetails,
    canUpdateDevices,
    canUpdateDeviceLocation,
    canDeleteDevices,
    canExportDevices,
    canManageProjects,
    canReadProjects,
    canCreateProjects,
    canUpdateProjects,
    canUpdateProjectStatus,
    canDeleteProjects,
    canReadProjectViewerScopes,
    canGrantProjectViewers,
    canUpdateProjectViewerAccounts,
    canUpdateProjectViewerAccountStatus,
    canAccessProjectViewerBoard,
    canAccessProjectViewerManagement,
    canAccessPackageManagement,
    canAccessModelManagement,
    canAccessModelValidation,
    canEvaluateModels,
    canActivateModels,
    canReadPackageDevices,
    canReadPaymentDetails,
    canReadMenderOtaConfig,
    canUpdateMenderOtaConfig,
    canReadDeviceUpgrades,
    canReadDeviceUpgradeBindings,
    canReadDeviceUpgradeBindingDetails,
    canReadDeviceUpgradeArtifacts,
    canReadDeviceUpgradeBatches,
    canReadDeviceUpgradeBatchDetails,
    canSyncDeviceUpgradeDevices,
    canCreateDeviceUpgradeArtifact,
    canUpdateDeviceUpgradeArtifact,
    canEnableDeviceUpgradeArtifact,
    canDisableDeviceUpgradeArtifact,
    canDeleteDeviceUpgradeArtifact,
    canCreateDeviceUpgradeBatch,
    canSyncDeviceUpgradeBatch,
    canCancelDeviceUpgradeBatch,
    isPlatformAccount,
    isProjectScopedAccount,
    canAccessProjectManagement,
    canAccessUserManagement: hasAnyPermissions(
      permissionUser,
      USER_MANAGEMENT_ACCESS_PERMISSIONS,
    ),
    canAccessRoleManagement: hasAnyPermissions(
      permissionUser,
      ROLE_MANAGEMENT_ACCESS_PERMISSIONS,
    ),
    canAccessRegionManagement: hasAnyPermissions(
      permissionUser,
      REGION_MANAGEMENT_ACCESS_PERMISSIONS,
    ),
    canAccessImageDetection: hasAnyPermissions(
      permissionUser,
      IMAGE_DETECTION_ACCESS_PERMISSIONS,
    ),
    canAccessSpeciesAnalysis: hasAnyPermissions(
      permissionUser,
      STATISTICS_SPECIES_ANALYSIS_ACCESS_PERMISSIONS,
    ),
    canAccessTrendAnalysis: hasAnyPermissions(
      permissionUser,
      STATISTICS_TREND_ANALYSIS_ACCESS_PERMISSIONS,
    ),
    canAccessStatistics: hasAnyPermissions(
      permissionUser,
      STATISTICS_ACCESS_PERMISSIONS,
    ),
    canAccessHistory: hasAnyPermissions(
      permissionUser,
      HISTORY_ACCESS_PERMISSIONS,
    ),
    canAccessOrderManagement: hasAnyPermissions(
      permissionUser,
      PAYMENT_ACCESS_PERMISSIONS,
    ),
    canAccessOperationLogs: hasAnyPermissions(
      permissionUser,
      OPERATION_LOG_ACCESS_PERMISSIONS,
    ),
    canAccessPaymentMerchantManagement: hasAnyPermissions(
      permissionUser,
      PAYMENT_MERCHANT_MANAGEMENT_ACCESS_PERMISSIONS,
    ),
    canAccessSystemSettings: hasAnyPermissions(
      permissionUser,
      SYSTEM_SETTINGS_ACCESS_PERMISSIONS,
    ),
    canAccessMenderOtaConfig: canReadMenderOtaConfig,
    canAccessOperationsCenter: canReadDeviceUpgrades,
    canAccessAnnotationManagement:
      hasAnyPermissions(permissionUser, HISTORY_ACCESS_PERMISSIONS) &&
      canReviewHistory,
    canAccessReviewExport:
      hasAnyPermissions(permissionUser, HISTORY_ACCESS_PERMISSIONS) &&
      canExportHistoryDataset,
    canAccessManualRecognitionPage:
      canAccessManualRecognition,
    canAccessDeviceManagement: hasAnyPermissions(
      permissionUser,
      DEVICE_ACCESS_PERMISSIONS,
    ),
    canAccessDeviceDistribution: hasAnyPermissions(
      permissionUser,
      DEVICE_DISTRIBUTION_ACCESS_PERMISSIONS,
    ),
    canAccessStatisticsExport: hasAnyPermissions(
      permissionUser,
      STATISTICS_EXPORT_ACCESS_PERMISSIONS,
    ),
  };
};
